import Model, { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import { tKey } from 'ember-intl';

export interface ReservationFilterOption {
  id: string;
  label: string;
}

// The full set of filter ids the reservations filters endpoint may return
// and that the property-tags filter dropdown/table know how to render/apply.
export const RESERVATION_FILTER_IDS = {
  HOTELS: 'hotels',
  PARENT_BRAND: 'parent_brand',
  BRAND: 'brand',
  STATE: 'state',
  CITY: 'city',
  HOTEL_CLASS: 'hotel_class',
} as const;

export type ReservationFilterId =
  (typeof RESERVATION_FILTER_IDS)[keyof typeof RESERVATION_FILTER_IDS];

type SubscriptionFilterAttr =
  | 'brand'
  | 'city'
  | 'hotelClass'
  | 'id'
  | 'parentBrand'
  | 'state';

// Single source of truth for filter metadata: the dropdown reads labelKey,
// the table reads subscriptionAttr. Add a new filter id here only.
export const RESERVATION_FILTER_CONFIG: Record<
  ReservationFilterId,
  { labelKey: string; subscriptionAttr: SubscriptionFilterAttr }
> = {
  [RESERVATION_FILTER_IDS.HOTELS]: {
    labelKey: tKey('reservations.filters.hotels'),
    subscriptionAttr: 'id',
  },
  [RESERVATION_FILTER_IDS.PARENT_BRAND]: {
    labelKey: tKey('reservations.filters.parent_brand'),
    subscriptionAttr: 'parentBrand',
  },
  [RESERVATION_FILTER_IDS.BRAND]: {
    labelKey: tKey('reservations.filters.brand'),
    subscriptionAttr: 'brand',
  },
  [RESERVATION_FILTER_IDS.STATE]: {
    labelKey: tKey('reservations.filters.state'),
    subscriptionAttr: 'state',
  },
  [RESERVATION_FILTER_IDS.CITY]: {
    labelKey: tKey('reservations.filters.city'),
    subscriptionAttr: 'city',
  },
  [RESERVATION_FILTER_IDS.HOTEL_CLASS]: {
    labelKey: tKey('reservations.filters.hotel_class'),
    subscriptionAttr: 'hotelClass',
  },
};

const SUPPORTED_FILTER_IDS: string[] = Object.values(RESERVATION_FILTER_IDS);

export function isSupportedReservationFilterId(
  id: string,
): id is ReservationFilterId {
  return SUPPORTED_FILTER_IDS.includes(id);
}

export default class ReservationFilter extends Model {
  [Type] = 'reservation-filter' as const;

  @attr declare options: ReservationFilterOption[];
}
